/**
 * Hand-rolled 12-column dashboard grid engine (G16 verdict — no grid library).
 * Pure functions only: every operation returns new arrays/objects and never
 * mutates its input, which is what makes optimistic-apply + rollback safe
 * (see applyLayoutResult).
 *
 * Semantics: classic vertical compaction — items keep their (y, x) relative
 * order, gaps collapse upward. A moved/resized item is "pinned": within its
 * target ROW it is placed first regardless of x (C1 fix — an exact-(y,x)-only
 * tie-break made collisions directionally asymmetric: a tile dragged leftward
 * onto a neighbor lost the slot that the same tile dragged rightward won).
 */
import type { DashboardTile, DashboardTileType, TileLayout } from '../types/dashboardTypes';

export const GRID_COLS = 12;

export const MIN_TILE_SIZE: Record<DashboardTileType, { w: number; h: number }> = {
  stat: { w: 2, h: 2 },
  note: { w: 2, h: 2 },
  chart: { w: 3, h: 3 },
  table: { w: 3, h: 3 },
  pivot: { w: 3, h: 3 },
  map: { w: 3, h: 3 },
};

export type GridItem = TileLayout & { id: string; minW: number; minH: number };

export const clampLayout = (
  layout: TileLayout,
  min: { w: number; h: number },
): TileLayout => {
  let w = Math.min(Math.max(layout.w, min.w), GRID_COLS);
  const h = Math.max(layout.h, min.h);
  let x = Math.max(0, layout.x);
  const y = Math.max(0, layout.y);
  if (x + w > GRID_COLS) {
    const fitted = GRID_COLS - x;
    if (fitted >= min.w) {
      w = fitted;
    } else {
      x = GRID_COLS - w;
    }
  }
  return { x, y, w, h };
};

const collides = (a: GridItem, b: GridItem): boolean =>
  a.id !== b.id && a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * Vertical compaction preserving (y, x) order. `pinnedId` is placed first
 * among items sharing its target row (any x) so a just-moved tile takes the
 * slot and the occupant flows below — symmetric for left- and rightward moves.
 * Output preserves the input array's item order (positions change, order not).
 */
const compactWithPriority = (items: GridItem[], pinnedId?: string): GridItem[] => {
  const sorted = [...items].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    if (a.id === pinnedId) return -1;
    if (b.id === pinnedId) return 1;
    if (a.x !== b.x) return a.x - b.x;
    return a.id < b.id ? -1 : 1;
  });
  const placed: GridItem[] = [];
  for (const original of sorted) {
    const candidate: GridItem = { ...original, y: 0 };
    while (placed.some((p) => collides(candidate, p))) candidate.y += 1;
    placed.push(candidate);
  }
  const byId = new Map(placed.map((p) => [p.id, p]));
  return items.map((i) => byId.get(i.id)!);
};

export const compactItems = (items: GridItem[]): GridItem[] => compactWithPriority(items);

const withItem = (
  items: GridItem[],
  id: string,
  change: (item: GridItem) => GridItem,
): GridItem[] => items.map((i) => (i.id === id ? change(i) : i));

export const moveTile = (
  items: GridItem[],
  id: string,
  target: { x: number; y: number },
): GridItem[] => {
  const moved = withItem(items, id, (i) => ({
    ...i,
    ...clampLayout({ ...i, x: target.x, y: target.y }, { w: i.minW, h: i.minH }),
  }));
  return compactWithPriority(moved, id);
};

export const resizeTile = (
  items: GridItem[],
  id: string,
  size: { w: number; h: number },
): GridItem[] => {
  const resized = withItem(items, id, (i) => ({
    ...i,
    ...clampLayout({ ...i, w: size.w, h: size.h }, { w: i.minW, h: i.minH }),
  }));
  return compactWithPriority(resized, id);
};

export type GridDirection = 'left' | 'right' | 'up' | 'down';

/**
 * One-unit keyboard move. Horizontal steps by one column; 'down' jumps past
 * the item's own height so compaction produces a swap; 'up' targets the row
 * of the nearest x-overlapping item above (C1 fix — a self-height jump was a
 * permanent dead-end past a taller neighbor: compaction pushed the item
 * straight back every press).
 */
export const keyboardMove = (
  items: GridItem[],
  id: string,
  direction: GridDirection,
): GridItem[] => {
  const current = items.find((i) => i.id === id);
  if (!current) return items;
  const target = { x: current.x, y: current.y };
  if (direction === 'left') target.x = Math.max(0, current.x - 1);
  if (direction === 'right') target.x = Math.min(GRID_COLS - current.w, current.x + 1);
  if (direction === 'down') target.y = current.y + current.h + 1;
  if (direction === 'up') {
    const above = items.filter(
      (i) => i.id !== id && i.y < current.y && i.x < current.x + current.w && current.x < i.x + i.w,
    );
    // Landing exactly on the neighbor's row triggers the pinned-first
    // tie-break, so the moved tile takes the slot and the neighbor flows below.
    target.y = above.length > 0 ? Math.max(...above.map((i) => i.y)) : 0;
  }
  return moveTile(items, id, target);
};

/** One-unit keyboard resize (Shift+arrow): right/down grow, left/up shrink. */
export const keyboardResize = (
  items: GridItem[],
  id: string,
  direction: GridDirection,
): GridItem[] => {
  const current = items.find((i) => i.id === id);
  if (!current) return items;
  const size = { w: current.w, h: current.h };
  if (direction === 'right') size.w += 1;
  if (direction === 'left') size.w -= 1;
  if (direction === 'down') size.h += 1;
  if (direction === 'up') size.h -= 1;
  return resizeTile(items, id, size);
};

/** Optimistic-apply helper: keep `next` when persistence succeeded, else roll back. */
export const applyLayoutResult = <T>(previous: T, next: T, persisted: boolean): T =>
  persisted ? next : previous;

/** Bridges Dashboard tiles into grid items (min size derives from tile type). */
export const gridItemsFromTiles = (tiles: DashboardTile[]): GridItem[] =>
  tiles.map((t) => ({
    id: t.id,
    ...t.layout,
    minW: MIN_TILE_SIZE[t.tileType].w,
    minH: MIN_TILE_SIZE[t.tileType].h,
  }));
